import React from 'react';
import { Page } from '../../typings/reviews';
import {
  TextCustomProps,
  TextAppearance,
  TextColor,
  TextSize,
  TextLineHeight,
  TextAlignment,
  dropdownOptionType,
  DropdownCustom,
  DropdownOption,
} from '@vw-marketing/us-components';
import _ from 'lodash';
import {
  DealerReviewsPagesContainer,
  ReviewsBarContainer,
  DropdownSectionContainer,
  StyledRatingDropdownWrapper,
  StyledSortDropdownWrapper,
} from './styled';
import { FeatureAppTrackingManager } from '../../services/use-tracking-manager';
import { AppConstants } from '../../utils/app-constants';
import { DealerReview } from '../../typings/dealer-review';
import DealerReviewsHandler from './dealer-reviews-handle';
import { atom, useRecoilState } from 'recoil';

interface DealerReviewsPagesProps {
  readonly reviews?: DealerReview;
  readonly trackingManager?: FeatureAppTrackingManager;
}

export const ratingOptions: dropdownOptionType[] = [
  { value: "all", label: 'All Stars' },
  { value: '1', label: '1 Star' },
  { value: '2', label: '2 Stars' },
  { value: '3', label: '3 Stars' },
  { value: '4', label: '4 Stars' },
  { value: '5', label: '5 Stars' },
];

export const typeOptions: dropdownOptionType[] = [
  { value: "all", label: 'All Categories' },
  { value: 'Service', label: 'Service' },
  { value: 'Sales', label: 'Sales' },
];

export const sortOptions: dropdownOptionType[] = [
  { value: 'newest', label: 'Newest' },
  { value: 'oldest', label: 'Oldest' },
];
interface AppFilters {
  type?: dropdownOptionType;
  sort?: dropdownOptionType;
  rating?: dropdownOptionType;
  page?: Page;
  pageNumber: number;
  reviewsDisplayed: number;
  reviewsOffset: number;
  isResetFilter: boolean;
  hasMoreReviews?: boolean;
  disableLoading?: boolean;
  wasSearch?: boolean;
  isLoadingMore?: boolean;
}
export const REVIEWS_OFFSET = AppConstants.API_PAGINATION_BASE as number;

export const filters = atom({
  key: 'filters',
  default: {
    type: undefined,
    sort: undefined,
    rating: undefined,
    page: {},
    pageNumber: AppConstants.API_PAGINATION_BASE,
    reviewsDisplayed: 0,
    reviewsOffset: REVIEWS_OFFSET,
    isResetFilter: true,
    disableLoading: false,
  } as AppFilters,
});

const DealerReviewsPages: React.FC<DealerReviewsPagesProps> = props => {
  const { reviews, trackingManager } = props;
  const [filtersState, setFiltersState] = useRecoilState(filters);
  const {  rating, sort, page, pageNumber, type } = filtersState;
  // Component constants
  const ratingLabel = 'Filter by Rating';
  const selectedRatingLabel = 'Filter by ';
  const typeLabel = 'Filter by Review Type';
  const selectedTypeLabel = 'Filter by ';

  const dropdownTextStyle: TextCustomProps = {
    appearance: TextAppearance.label150,
    style: {
      color: TextColor.inherit,
      fontSize: TextSize.TextSize16,
      lineHeight: TextLineHeight.TextLineHeight20,
      textAlign: TextAlignment.left,
    },
  };

  const onHandleViewMore = () => {
    setFiltersState({
      ...filtersState,
      wasSearch: false,
      disableLoading: true,
      isLoadingMore: true,
    });
  };

  /**
   * Handle new request for rating selected
   * @param rating
   */
  const handleRatingChange = (value: dropdownOptionType) => {
    setFiltersState({
      ...filtersState,
      rating: value.value === "all" ? undefined : value,
      sort: undefined,
      isResetFilter: true,
      reviewsOffset: REVIEWS_OFFSET,
      wasSearch: false,
    });
  };
  const handleTypeChange = (value: dropdownOptionType) => {
    setFiltersState({
      ...filtersState,
      sort: undefined,
      type: value.value === "all" ? undefined : value,
      isResetFilter: true,
      reviewsOffset: REVIEWS_OFFSET,
      page: { number: 1, size: 10, totalElements: 100, totalPages: 10 },
      wasSearch: false,
    });
  };

  /**
   * Reset all applied filters
   */
  const handleResetFilters = () => {
    setFiltersState({
      ...filtersState,
      rating: undefined,
      type: undefined,
      sort: undefined,
      isResetFilter: true,
      wasSearch: false,
      reviewsOffset: REVIEWS_OFFSET ,
    });
  };

  React.useEffect(() => {
    return () => {
      handleResetFilters();
    };
  }, []);
  if (!reviews) return <></>;
  return (
    <DealerReviewsPagesContainer>
      <ReviewsBarContainer>
        <DropdownSectionContainer>
          <StyledSortDropdownWrapper tabIndex={0}>
            <DropdownCustom
              value={type}
              placeholder={typeLabel}
              selectedLabel={selectedTypeLabel}
              onChange={handleTypeChange}
              inputTextAppearance={dropdownTextStyle}
            >
              {typeOptions.map((option: dropdownOptionType) => (
                <DropdownOption
                  value={option.value}
                  label={option.label}
                  key={option.value}
                />
              ))}
            </DropdownCustom>
          </StyledSortDropdownWrapper>
          <StyledRatingDropdownWrapper tabIndex={0}>
            <DropdownCustom
              value={rating}
              selectedLabel={selectedRatingLabel}
              placeholder={ratingLabel}
              onChange={handleRatingChange}
              inputTextAppearance={dropdownTextStyle}
            >
              {ratingOptions.map((option: dropdownOptionType) => (
                <DropdownOption
                  value={option.value}
                  label={option.label}
                  key={option.value}
                />
              ))}
            </DropdownCustom>
          </StyledRatingDropdownWrapper>
        </DropdownSectionContainer>
      </ReviewsBarContainer>
      <DealerReviewsHandler
        dealerReviews={reviews}
        pageNumber={pageNumber}
        pageData={page}
        reverse={sort ? sort.value === 'oldest' : undefined}
        rating={rating?.value}
        handleResetFilters={handleResetFilters}
        trackingManager={trackingManager}
        onHandleMore={onHandleViewMore}/>
    </DealerReviewsPagesContainer>
  );
};

export default DealerReviewsPages;
